import React, { useState, useEffect } from 'react';
import SEO from '../components/SEO';
import { Link, useLocation } from 'react-router-dom';
import { useMobileApp } from '../utils/MobileAppContext';
import {
  ArrowRight,
  Box,
  MapPin,
  DollarSign
} from 'lucide-react';
import { API_BASE_URL } from '../utils/api';
import { ReactComponent as SharePointIcon } from '../assets/sharepoint.svg';
import { ReactComponent as PAIcon } from '../assets/PowerApps_scalable.svg';
import { ReactComponent as PPIcon } from '../assets/PowerPages_scalable.svg';
import { ReactComponent as PBIcon } from '../assets/Icon/PowerBI.svg';
import { ReactComponent as CSIcon } from '../assets/CopilotStudio_scalable.svg';
import { ReactComponent as AzureSer } from '../assets/azure_icon.svg';
import { ReactComponent as SQLIcon } from '../assets/Database.svg';
import { ReactComponent as PTAIcon } from '../assets/PowerAutomate_scalable.svg';
import '../assets/icon.css';
import { ReactComponent as SSecurity } from '../assets/Defender.svg';
import { ReactComponent as CloudService } from '../assets/Cloud_Services.svg';
import { ReactComponent as CDatabase } from '../assets/Cloud_Database.svg';
import { ReactComponent as CustomDev } from '../assets/CustomDev.svg';
import { ReactComponent as FlutterApp } from '../assets/flutter_app.svg';
import { ReactComponent as Support } from '../assets/Support.svg';
import { ReactComponent as UIDesign } from '../assets/UIDesign.svg';
import { ReactComponent as Digitalmarketing } from '../assets/digitalmarketing.svg';
import Logo from '../components/devNectar_new_logo.png';


const Home: React.FC = () => {
  const { setHighlight, isHighlighted } = useMobileApp();
  const location = useLocation();
  const [currentGroup, setCurrentGroup] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const [heroTextIndex, setHeroTextIndex] = useState(0);
  const [featuredJobs, setFeaturedJobs] = useState<any[]>([]);
  
  const heroTexts = ["For your Needs", "For the Future", "With Perfection"];

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/jobs`);
        if (res.ok) {
          const allJobs = await res.json();
          const featured = allJobs.filter((job: any) => job.showOnHome);
          setFeaturedJobs(featured);
        }
      } catch (error) {
        console.error('Failed to fetch jobs for home screen', error);
      }
    };
    fetchJobs();
  }, []);
  
  // Group icons into sets of 8 for batch rotation
  const GROUP_SIZE = 8;
  const totalGroups = Math.ceil(16 / GROUP_SIZE); // 16 technologies total

  useEffect(() => {
    const interval = setInterval(() => {
      setIsVisible(false); // Start fade out
      setTimeout(() => {
        setCurrentGroup(prev => (prev + 1) % totalGroups);
        setIsVisible(true); // Start fade in
      }, 800); 
    }, 7000); // 7 seconds per batch
    return () => clearInterval(interval);
  }, [totalGroups]);

  useEffect(() => {
    const textInterval = setInterval(() => {
      setHeroTextIndex((prev) => (prev + 1) % heroTexts.length);
    }, 6000); // Matches the 6s typing animation duration
    return () => clearInterval(textInterval);
  }, [heroTexts.length]);






  const technologies = [
    { name: 'SharePoint', icon: <SharePointIcon className="icon-common" /> },
    { name: 'Power Apps', icon: <PAIcon className="icon-common" /> },
    { name: 'Power Automate', icon: <PTAIcon className="icon-common" /> },
    { name: 'Power Pages', icon: <PPIcon className="icon-common" /> },
    { name: 'Power BI', icon: <PBIcon className="icon-common" /> },
    { name: 'Copilot Studio', icon: <CSIcon className="icon-common" /> },
    { name: 'Azure', icon: <AzureSer className="icon-common" /> },
    { name: 'SQL', icon: <SQLIcon className="icon-common" /> },
    { name: 'React', icon: <span role="img" aria-label="React">⚛️</span> },
    { name: 'Node.js', icon: <span role="img" aria-label="Node.js">🟢</span> },
    { name: 'Python', icon: <span role="img" aria-label="Python">🐍</span> },
    { name: 'AWS', icon: <span role="img" aria-label="AWS">☁️</span> },
    { name: 'Docker', icon: <span role="img" aria-label="Docker">🐳</span> },
    { name: 'PostgreSQL', icon: <span role="img" aria-label="PostgreSQL">🐘</span> },
    { name: 'MongoDB', icon: <span role="img" aria-label="MongoDB">🍃</span> },
    { name: 'Flutter', icon: <FlutterApp className="icon-common" /> },
  ];



  const technologiesForSection = [
    { name: 'SharePoint', icon: <SharePointIcon className="icon-common" />, category: 'Frontend' },
    { name: 'Power Apps', icon: <PTAIcon className="icon-common" />, category: 'Low Code' },
    { name: 'Power Automate', icon: <PAIcon className="icon-common" />, category: 'Low Code' },
    { name: 'Power Pages', icon: <PPIcon className="icon-common" />, category: 'Low Code' },
    { name: 'Power BI', icon: <PBIcon className="icon-common" />, category: 'Low Code' },
    { name: 'Copilot Studio', icon: <CSIcon className="icon-common" />, category: 'Low Code' },
    { name: 'Azure', icon: <AzureSer className="icon-common" />, category: 'Cloud' },
    { name: 'SQL', icon: <SQLIcon className="icon-common" />, category: 'Database' },
    { name: 'React', icon: '⚛️', category: 'Frontend' },
    { name: 'Node.js', icon: '🟢', category: 'Backend' },
    { name: 'Python', icon: '🐍', category: 'Backend' },
    { name: 'AWS', icon: '☁️', category: 'Cloud' },
    { name: 'Docker', icon: '🐳', category: 'DevOps' },
    { name: 'PostgreSQL', icon: '🐘', category: 'Database' },
    { name: 'MongoDB', icon: '🍃', category: 'Database' },
    { name: 'Flutter', icon: <FlutterApp className="icon-common" />, category: 'Mobile' }
  ];

  const services = [
    {
      icon: <CustomDev className="icon-common2" />,
      title: 'Custom Development',
      description: 'Tailored software solutions built with modern technologies to meet your specific business needs.',
      link: '/services#custom-web-development'
    },
    {
      icon: <FlutterApp className="icon-common2" />,
      title: 'Mobile Development',
      description: 'Native and cross-platform mobile apps that deliver exceptional user experiences.',
      link: '/services#mobile-app-development'
    },
    {
      icon: <CloudService className="icon-common2" />,
      title: 'Cloud Solutions',
      description: 'Scalable cloud infrastructure and migration services for optimal performance.',
      link: '/services#cloud-solutions'
    },
    {
      icon: <CDatabase className="icon-common2" />,
      title: 'Database Design',
      description: 'Robust database architecture and optimization for efficient data management.',
      link: '/services#database-solutions'
    },
    {
      icon: <Support className="icon-common2" />,
      title: 'Security & Support',
      description: '24/7 monitoring, security implementation, and ongoing technical support.',
      link: '/services#devops-infrastructure'
    },
    {
      icon: <SSecurity className="icon-common2" />,
      title: 'Cyber Security',
      description: 'Cybersecurity Risk Management and assessment, Information Security Management System..',
      link: '/services#cyber-security'
    },
    {
      icon: <UIDesign className="icon-common2" />,
      title: 'Graphics and Visualization',
      description: 'Creating engaging and user-friendly interfaces for a seamless experience.',
      link: '/services#graphics-visualization'
    },
    {
      icon: <Digitalmarketing className="icon-common2" />,
      title: 'Performance Marketing',
      description: 'We utilize YouTube, Instagram, Facebook, and Google Ads to engage your target audience, acquiring customers at surprisingly economical CPAs.',
      link: '/services#performance-marketing'
    },
    {
      icon: <Box className="icon-common2 text-blue-600" />,
      title: '3D scientific visualization using Babylon.js and Three.js',
      description: 'Creating engaging and user-friendly interfaces for a seamless experience. We specialize in creating interactive 3D visualizations of complex simulations using ThreeJs and Babylon.',
      link: '/services#3d-scientific-visualization'
    }
  ];

  const processSteps = [
    {
      step: '01',
      title: 'Discovery',
      description: 'We analyze your requirements and business goals to create the perfect strategy.'
    },
    {
      step: '02',
      title: 'Design & Planning',
      description: 'Detailed planning and architecture design for optimal user experience.'
    },
    {
      step: '03',
      title: 'Development',
      description: 'Agile development with regular updates and transparent communication.'
    },
    {
      step: '04',
      title: 'Testing & Launch',
      description: 'Comprehensive testing followed by smooth deployment and launch.'
    }
  ];

  return (
    <div className="min-h-screen">
      <SEO
        title="Home"
        description="devNectar provides complete software development services. Specializing in SharePoint, Power Platform, React, Node.js, and Cloud Solutions."
        keywords="Software Development, SharePoint, Power Platform, React Developer, Node.js Expert, Cloud Solutions, IT Support"
      />
      {/* Hero Section */}
      <section data-id="hero-section" className="relative bg-gradient-to-br from-blue-900 via-blue-900 to-indigo-900 text-white overflow-hidden py-2 md:py-4 lg:py-6">
        {/* Rain Animation Layer */}
        <div className="absolute inset-0 pointer-events-none select-none z-0">
          <div className="absolute inset-0 backdrop-blur-[2px] opacity-30"></div>
          
          {/* Rain Drops */}

        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Left Side: Content */}
            <div data-id="hero-content" className="text-center lg:text-left z-10 animate-fade-in-up">
              <h2 data-id="hero-title" className="mb-4 leading-tight flex flex-col items-center lg:items-start">
                <span className="text-4xl md:text-5xl lg:text-6xl font-bold mb-2 flex flex-col md:flex-row md:gap-3 items-center lg:items-start">
                  <span>Software</span>
                  <span>Development</span>
                </span>
                <span key={heroTextIndex} className="text-3xl md:text-4xl lg:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-400 animate-typing overflow-hidden whitespace-nowrap border-r-4 border-blue-400 pr-1">
                  {heroTexts[heroTextIndex]}
                </span>
              </h2>
              <p data-id="hero-description" className="text-lg md:text-xl text-gray-300 mb-8 max-w-2xl mx-auto lg:mx-0 leading-relaxed font-medium">
                Professional solutions, expert support, and cutting-edge development across all modern technologies.
                We turn your ideas into reality with precision and innovation.
              </p>
              <div data-id="hero-cta" className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Link
                  to="/services"
                  data-id="hero-services-btn"
                  className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-600 text-white font-semibold rounded-lg hover:from-blue-700 hover:to-blue-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                >
                  Explore Services
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
                <Link
                  to="/contact"
                  data-id="hero-contact-btn"
                  className="inline-flex items-center px-8 py-4 bg-transparent border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-blue-900 transition-all duration-300"
                >
                  Get Started
                </Link>
              </div>
            </div>
            {/* Right Side: 3D Icons Layout */}
            <div className="relative h-[300px] md:h-[420px] w-full flex flex-col items-center justify-center mt-4 lg:mt-0 lg:ml-0">
              {/* Background Glow */}
              <div className="absolute w-56 h-56 bg-blue-500/20 rounded-full blur-[90px] animate-pulse"></div>
              
              <style>{`
                .circular-layout {
                  position: relative;
                  width: 340px;
                  height: 340px;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  transform-style: preserve-3d;
                }
                .icon-3d-card {
                  position: absolute;
                  width: 56px;
                  height: 56px;
                  background: white;
                  border-radius: 50%;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  box-shadow: 
                    0 12px 24px rgba(0,0,0,0.2),
                    inset -2px -3px 10px rgba(0,0,0,0.1),
                    inset 2px 3px 10px rgba(255,255,255,0.8);
                  transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1);
                  cursor: pointer;
                  z-index: 2;
                }
                .icon-3d-card:hover {
                  transform: scale(1.2) translateZ(40px) !important;
                  box-shadow: 0 20px 40px rgba(0,0,0,0.3);
                  z-index: 10;
                }
                .icon-3d-card svg {
                  width: 28px !important;
                  height: 28px !important;
                  display: block;
                }
                .icon-3d-card span {
                  font-size: 32px;
                  line-height: 1;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  width: 100%;
                  height: 100%;
                }
                @keyframes typing {
                  0% { width: 0; }
                  50% { width: 100%; }
                  100% { width: 0; }
                }
                @keyframes blink {
                  50% { border-color: transparent; }
                }
                .animate-typing {
                  animation: typing 6s steps(20) infinite, blink 0.8s infinite;
                  max-width: fit-content;
                }
                @keyframes ripple-expand {
                  0% { width: 0; height: 0; opacity: 0.7; }
                  100% { width: 150px; height: 150px; opacity: 0; }
                }
                .animate-ripple-expand {
                  animation: ripple-expand 2s ease-out forwards;
                  border-width: 2px;
                }
                @keyframes orbit-slow {
                  from { transform: rotate(0deg); }
                  to { transform: rotate(360deg); }
                }
                .animate-orbit-slow {
                  animation: orbit-slow 40s linear infinite;
                }
                @keyframes float-hero {
                  0%, 100% { transform: translateY(0); }
                  50% { transform: translateY(-15px); }
                }
                .animate-float-hero {
                  animation: float-hero 5s ease-in-out infinite;
                }
                .batch-transition {
                  transition: opacity 0.8s ease-in-out, transform 0.8s ease-in-out;
                }
                .service-card-radiant {
                  position: relative;
                  background: white;
                  border-radius: 24px;
                   padding: 1.75rem;
                  border: 1.5px solid #cbd5e1;
                  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
                  overflow: hidden;
                  z-index: 1;
                }
                .service-card-radiant::before {
                  content: '';
                  position: absolute;
                  bottom: -100%;
                  left: 0;
                  width: 100%;
                  height: 100%;
                  background: linear-gradient(to top, #eff6ff, white);
                  transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
                  z-index: -1;
                }
                @keyframes counter-orbit {
                  from { transform: rotate(0deg); }
                  to { transform: rotate(-360deg); }
                }
                .animate-counter-orbit {
                  animation: counter-orbit 40s linear infinite;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  width: 100%;
                  height: 100%;
                }
                .service-card-radiant:hover::before {
                  bottom: 0;
                }
                .service-card-radiant:hover {
                  transform: translateY(-8px) scale(1.02);
                  border-color: #3b82f6;
                  box-shadow: 0 20px 40px rgba(59, 130, 246, 0.1);
                }
                .radiant-icon-wrapper {
                  width: 64px;
                  height: 64px;
                  background: white;
                  border-radius: 50%;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  color: #3b82f6;
                   margin-bottom: 1.25rem;
                  transition: all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
                  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
                }
                .service-card-radiant:hover .radiant-icon-wrapper {
                  background: white;
                  color: #3b82f6;
                  transform: scale(1.1);
                  box-shadow: 0 10px 20px rgba(59, 130, 246, 0.15);
                }
                .radiant-title {
                  position: relative;
                  display: inline-block;
                  margin-bottom: 1rem;
                }
                .radiant-title::after {
                  content: '';
                  position: absolute;
                  bottom: -10px;
                  left: 0;
                  width: 0;
                  height: 3px;
                  background: #3b82f6;
                  transition: width 0.4s ease;
                  border-radius: 10px;
                }
                .service-card-radiant:hover .radiant-title::after {
                  width: 100%;
                }
                .action-link {
                  margin-top: auto;
                  display: flex;
                  align-items: center;
                  gap: 8px;
                  color: #3b82f6;
                  font-weight: 700;
                  transition: all 0.3s ease;
                }
                .service-card-radiant:hover .action-link {
                  gap: 15px;
                  transform: translateX(5px);
                }
                @media (max-width: 640px) {
                  .circular-layout { width: 320px; height: 320px; }
                  .icon-3d-card { width: 52px; height: 52px; }
                  .icon-3d-card svg, .icon-3d-card span { width: 26px !important; height: 26px !important; }
                }

                .dynamic-tagline {
                  color: #1e3a8a;
                  display: inline-block;
                }
              `}</style>

              <div className="circular-layout animate-float-hero">
                {/* Central DevNectar Logo */}
                <Link to="/" className="absolute z-20 w-28 h-28 rounded-full bg-white p-0 shadow-[0_0_40px_rgba(59,130,246,0.15)] flex items-center justify-center border-4 border-blue-50/80 transform transition-transform hover:scale-110 duration-500">
                  <img src={Logo} alt="DevNectar Logo" className="w-full h-full object-contain rounded-full" />
                </Link>

                {/* Rotating Icons Ring */}
                <div className={`relative w-full h-full animate-orbit-slow batch-transition ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>
                  {technologies.slice(currentGroup * 8, (currentGroup + 1) * 8).map((tech, i) => {
                    const radius = window.innerWidth < 640 ? 120 : 150;
                    const angle = (i * (360 / 8)) * (Math.PI / 180);
                    const x = radius * Math.cos(angle);
                    const y = radius * Math.sin(angle);
                    
                    return (
                      <div
                        key={i}
                        className="icon-3d-card"
                        style={{
                          left: `calc(50% + ${x}px - 28px)`,
                          top: `calc(50% + ${y}px - 28px)`,
                        }}
                        title={tech.name}
                        role="img"
                        aria-label={tech.name}
                      >
                        <div className="animate-counter-orbit">
                          {tech.icon}
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                {/* Decorative orbit line */}
                <div className="absolute inset-0 rounded-full border border-blue-400/10 pointer-events-none"></div>
              </div>

              {/* Tagline under the circle */}
              <div className="mt-8 md:mt-12 text-center animate-fade-in-up" style={{ animationDelay: '0.5s' }}>
                <p className="bg-white/90 px-6 py-2 rounded-full border border-blue-200 shadow-md flex items-center justify-center">
                  <span className="dynamic-tagline font-bold tracking-[0.05em] text-[11px] md:text-sm">
                    Transforming Business with AI, Automation & Innovation
                  </span>
                </p>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section data-id="services-section" className="pt-12 pb-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 reveal">
            <h2 data-id="services-title" className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">
              <span className="heading-accent-line">Our Services</span>
            </h2>
            <p data-id="services-description" className="text-xl text-gray-600 max-w-3xl mx-auto reveal-text-sub">
              Comprehensive software development services designed to accelerate your business growth.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => {
              return (
                <div
                  key={index}
                  data-id={`service-${index}`}
                  onClick={() => setHighlight(location.pathname, `service-${index}`)}
                  className={`service-card-radiant group reveal reveal-delay-${(index % 3) + 1} ${
                    isHighlighted(location.pathname, `service-${index}`) 
                      ? 'ring-4 ring-blue-500/50 shadow-2xl scale-[1.02] bg-blue-50/30' 
                      : ''
                  }`}
                >
                  <div className="flex flex-col h-full">
                    <div className={`radiant-icon-wrapper text-3xl ${
                      isHighlighted(location.pathname, `service-${index}`) ? 'bg-blue-600 text-white' : ''
                    }`}>
                      {service.icon}
                    </div>
                    <h3 className="radiant-title text-2xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors">
                      {service.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed text-lg mb-5 transition-colors">
                      {service.description}
                    </p>
                    <Link to={service.link} className="action-link">
                      <span>Learn More</span>
                      <ArrowRight className="w-5 h-5" />
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="text-center mt-8">
            <Link
              to="/services"
              data-id="services-cta"
              className="btn-premium group inline-flex items-center px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-600 text-white font-bold rounded-lg hover:from-blue-700 hover:to-blue-700 transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              <span>View All Services</span>
              <ArrowRight className="ml-2 h-5 w-5 btn-arrow-bounce" />
            </Link>
          </div>
        </div>
      </section>

      {/* Technologies Section */}
      <section data-id="technologies-section" className="pt-12 pb-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 reveal">
            <h2 data-id="tech-title" className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">
              <span className="heading-accent-line">Technologies We Master</span>
            </h2>
            <p data-id="tech-description" className="text-xl text-gray-600 max-w-3xl mx-auto reveal-text-sub">
              From frontend frameworks to cloud infrastructure, we work with all modern technologies to deliver the best solutions.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-6">
            {technologiesForSection.map((tech, index) => (
              <div
                key={index}
                data-id={`tech-${tech.name.toLowerCase()}`}
                className={`tech-card-premium rounded-xl p-6 text-center reveal reveal-delay-${(index % 8) + 1}`}
              >
                <div className="flex items-center justify-center text-4xl mb-4 tech-icon-float">
                  {tech.icon}
                </div>
                <h3 className="font-bold text-gray-900 mb-1">{tech.name}</h3>
                <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">{tech.category}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section data-id="process-section-alt" className="pt-12 pb-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 reveal">
            <h2 data-id="process-title" className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">
              <span className="heading-accent-line">Our Development Process</span>
            </h2>
            <p data-id="process-description" className="text-xl text-gray-600 max-w-3xl mx-auto reveal-text-sub">
              A proven methodology that ensures quality delivery and client satisfaction at every step.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {processSteps.map((step, index) => (
              <div
                key={index}
                data-id={`process-${index}`}
                className="text-center group reveal reveal-delay"
              >
                <div className="process-node-container">
                  <div className="process-ring-dashed"></div>
                  <div className="process-ring-solid"></div>
                  <div className="process-step-node text-xl">
                    {step.step}
                  </div>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors uppercase tracking-wide">
                  {step.title}
                </h3>
                <p className="text-gray-600 leading-relaxed font-medium">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Careers Section - Temporarily Disabled */}
      {false && featuredJobs.length > 0 && (
        <section data-id="featured-careers-section" className="pt-8 pb-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12 reveal active">
              <h2 data-id="careers-title" className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">
                <span className="heading-accent-line">Explore Latest Openings</span>
              </h2>
              <p data-id="careers-description" className="text-xl text-gray-600 max-w-3xl mx-auto reveal-text-sub">
                Join our team of passionate professionals and build the future of enterprise software.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {featuredJobs.map((job) => (
                <div
                  key={job.id}
                  data-id={`featured-job-${job.id}`}
                  className="bg-white rounded-3xl p-6 shadow-sm border border-gray-200/80 hover:border-blue-500 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1.5 flex flex-col justify-between group"
                >
                  <div>
                    <div className="flex justify-between items-start mb-4">
                      <span className="px-3 py-1 bg-blue-50 text-blue-700 text-xs font-semibold rounded-full">
                        {job.department}
                      </span>
                      <span className="px-3 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">
                        {job.type}
                      </span>
                    </div>

                    <h3 className="text-xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors mb-2">
                      {job.title}
                    </h3>

                    <div className="flex flex-wrap gap-y-2 gap-x-4 text-gray-500 text-sm mb-4">
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4 text-blue-500" />
                        {job.location}
                      </span>
                      {job.salary && (
                        <span className="flex items-center gap-1 font-semibold text-gray-700">
                          <DollarSign className="w-4 h-4 text-emerald-500" />
                          {job.salary}
                        </span>
                      )}
                    </div>

                    <p className="text-gray-600 text-sm line-clamp-3 mb-6">
                      {job.description}
                    </p>
                  </div>

                  <div className="pt-4 border-t border-gray-100 flex items-center justify-between">
                    <div className="flex flex-wrap gap-1.5 max-w-[70%]">
                      {job.tags && job.tags.slice(0, 2).map((tag: string, idx: number) => (
                        <span key={idx} className="px-2 py-0.5 bg-gray-50 text-gray-500 text-[11px] font-medium rounded border border-gray-200">
                          {tag}
                        </span>
                      ))}
                    </div>
                    <Link
                      to="/careers"
                      className="inline-flex items-center gap-1 text-sm font-bold text-blue-600 group-hover:text-blue-700 hover:gap-2 transition-all"
                    >
                      Apply Now
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center mt-12">
              <Link
                to="/careers"
                data-id="careers-view-all-cta"
                className="btn-premium group inline-flex items-center px-8 py-4 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700 transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                <span>View All Openings</span>
                <ArrowRight className="ml-2 h-5 w-5 btn-arrow-bounce" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section data-id="cta-section" className="relative py-8 bg-gradient-to-br from-blue-900 via-blue-900 to-indigo-900 text-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center reveal">
          <h2 data-id="cta-title" className="text-3xl md:text-4xl font-bold text-white mb-3">
            <span className="heading-accent-line">Ready to Start Your Project?</span>
          </h2>
          <p data-id="cta-description" className="text-xl text-blue-200 mb-6 max-w-3xl mx-auto reveal-text-sub">
            Let's discuss your requirements and create something amazing together.
            Our team is ready to turn your vision into reality.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              data-id="cta-contact-btn"
              className="relative btn-premium group inline-flex items-center px-8 py-4 bg-white text-blue-900 font-bold rounded-lg hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              {/* Radiation Effect */}
              <div className="absolute inset-0 rounded-lg border-2 border-white opacity-50 animate-[ping_2s_cubic-bezier(0,0,0.2,1)_infinite] -z-10 bg-white shadow-xl shadow-white/50"></div>
              <div className="absolute inset-0 rounded-lg border-2 border-white opacity-40 animate-[ping_2s_cubic-bezier(0,0,0.2,1)_infinite_1s] -z-10"></div>
              
              <span>Get Free Consultation</span>
              <ArrowRight className="ml-2 h-5 w-5 btn-arrow-bounce" />
            </Link>
            <Link
              to="/services"
              data-id="cta-services-btn"
              className="btn-premium group inline-flex items-center px-8 py-4 bg-transparent border-2 border-white text-white font-bold rounded-lg hover:bg-white hover:text-blue-900 transition-all duration-300"
            >
              <span>View Our Work</span>
              <ArrowRight className="ml-2 h-5 w-5 btn-arrow-bounce" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;